import { Link } from "react-router-dom";
import { InsightSection } from "./InsightSection";
import { PipelineDiagram } from "./PipelineDiagram";

const Prose = ({ children }) => (
    <div className="max-w-prose space-y-3 text-base leading-relaxed text-gray-700">{children}</div>
);

const Code = ({ children }) => <code className="font-mono text-sm text-gray-800">{children}</code>;

export const HowThisIsBuilt = () => (
    <InsightSection
        id="how-this-is-built"
        title="How this is built"
        intro="Steam's APIs only describe the present: what you own, how long you've played it and which achievements you have. Everything here is reshaped from that into history."
    >
        <PipelineDiagram />
        <div className="mt-6">
            <Prose>
                <p>
                    The models are plain SQL files in dbt's <Code>stg_</Code> → <Code>int_</Code> →{" "}
                    <Code>mart_</Code> layout under <Code>etl/sql/models/steam/</Code>, applied by a small runner
                    that doesn't care which database it talks to. In production they build on Postgres. The tests
                    build the same files on an in-memory DuckDB database, which checks the logic and proves the SQL
                    is portable.
                </p>
                <p>
                    That portability is the point: the next step is to run these models on the{" "}
                    <Link to="/duckdb" className="font-semibold text-gray-800 underline decoration-gray-300 underline-offset-4 hover:text-gray-900">
                        DuckDB analytics engine
                    </Link>{" "}
                    alongside the pool data, and eventually to hand them to dbt.
                </p>
            </Prose>
        </div>
    </InsightSection>
);

export const WhyNoSnapshots = () => (
    <InsightSection id="why-no-snapshots" title="Why no snapshots">
        <Prose>
            <p>
                The obvious way to chart change is to copy the library every day and compare. For a library of a
                few hundred games that's a few hundred rows a day, nearly all identical to the day before, and it
                only starts recording from the day you switch it on.
            </p>
            <p>
                Instead, the history comes from events Steam already dates. Every achievement has an unlock time.
                The last-played timestamp shows a game had been played by then. The daily refresh also notes the
                first time it sees playtime on a game. Each of these proves a game was started{" "}
                <em>no later than</em> that moment, so the earliest one is the best estimate of when it started.
            </p>
            <p>
                That means an achievement from nine months ago beats a refresh that has only been watching for
                three, with no special cases. Games with no evidence at all — unplayed purchases — never enter
                the denominator, so buying ten games on a Monday doesn't drag the average down.
            </p>
            <p>
                The cost: anything that doesn't leave a dated event can't be recovered. There's no way to know how
                many hours you played on a given day, or what a global unlock rate was last year.
            </p>
        </Prose>
    </InsightSection>
);

export const ReadingTheNumbers = () => (
    <InsightSection id="reading-the-numbers" title="Reading the numbers">
        <Prose>
            <p>
                <strong>Steam average</strong> uses the same method as the "Avg. Game Completion Rate" on a Steam
                profile: the mean of each game's completion %, across games where you've unlocked at least one
                achievement. A game joins the average at its first unlock. Like the headline figures on the{" "}
                <Link to="/steam/games" className="underline">library page</Link>, it leaves out free and
                profile-limited games, so it can sit slightly below the figure on Steam. The other metrics change who counts and how much;
                their definitions sit under the metric picker and come from the same registry the API uses.
            </p>
            <p>
                Each point shows the value at the end of that day, week, month or year. A period with no activity
                carries the previous value forward, because nothing changed.
            </p>
            <p>
                Only library games count, using the same rules as the{" "}
                <Link to="/steam/games" className="font-semibold text-gray-800 underline decoration-gray-300 underline-offset-4 hover:text-gray-900">
                    Steam library page
                </Link>
                : no DLC, soundtracks or free-to-play games.
            </p>
        </Prose>
    </InsightSection>
);

export const Limitations = ({ meta }) => {
    const undated = meta?.undated_unlocks ?? 0;
    return (
        <InsightSection id="limitations" title="Limitations">
            <Prose>
                <ul className="list-disc space-y-3 pl-5">
                    <li>
                        <strong>Start dates before tracking are estimates.</strong> For a game without achievements,
                        the only API evidence is your most recent session, so its start date moves later every time
                        you play it — until the refresh sees it played for the first time and records that.
                    </li>
                    <li>
                        <strong>Some unlocks have no date.</strong> When Steam's main achievement endpoint fails, a
                        fallback returns which achievements you have but not when.{" "}
                        {undated > 0
                            ? `${undated} unlock${undated === 1 ? "" : "s"} came from that fallback. They`
                            : "Those unlocks"}{" "}
                        count towards today's totals but can't be placed on the timeline, so the latest point can sit
                        a little below the library page.
                    </li>
                    <li>
                        <strong>New achievements rewrite the past.</strong> When a game adds achievements (often
                        with DLC), its total rises everywhere on the timeline, so a game you finished years ago can
                        show below 100% for its whole history.
                    </li>
                    <li>
                        <strong>Community rates are today's.</strong> Global unlock percentages come from the latest
                        refresh, not from when you earned the achievement.
                    </li>
                    <li>
                        <strong>Release dates are free text.</strong> The Store gives strings like "12 Nov, 2019" or
                        "Coming soon". Partial dates fall back to the start of the month or year; others are left
                        blank.
                    </li>
                </ul>
            </Prose>
        </InsightSection>
    );
};
